import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Loader2, TrendingUp, AlertTriangle, Clock, Ban, HandshakeIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_CONFIG = {
  critical: { color: 'bg-red-500/10 text-red-700 border-red-200', dot: 'bg-red-500' },
  warning: { color: 'bg-amber-500/10 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  info: { color: 'bg-blue-500/10 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
};

export function AnalyticsDialog({ open, onOpenChange, clone }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!clone?.id || !open) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/persons/${clone.id}/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      toast.error('Erreur chargement analytics');
    } finally {
      setLoading(false);
    }
  }, [clone?.id, open, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="analytics-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg" data-testid="analytics-dialog-title">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            Suivi — {clone?.name || ''}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">Promesses, blocages et alertes</p>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : data ? (
          <div className="space-y-4">
            {/* Metric cards */}
            <div className="grid grid-cols-2 gap-3" data-testid="analytics-metrics">
              <MetricCard
                title="Promesses"
                icon={HandshakeIcon}
                iconColor="text-amber-500"
                items={[
                  { label: 'Actives', value: data.promesses.active, color: 'text-amber-600' },
                  { label: 'En retard', value: data.promesses.overdue, color: 'text-red-600', bold: true },
                  { label: 'Resolues', value: data.promesses.resolved, color: 'text-emerald-600' },
                ]}
                testId="metric-promesses"
              />
              <MetricCard
                title="Blocages"
                icon={Ban}
                iconColor="text-red-500"
                items={[
                  { label: 'Actifs', value: data.blocages.active, color: 'text-red-600' },
                  { label: 'Longs (>14j)', value: data.blocages.long_duration, color: 'text-red-700', bold: true },
                  { label: 'Resolus', value: data.blocages.resolved, color: 'text-emerald-600' },
                ]}
                testId="metric-blocages"
              />
            </div>

            {/* Alerts */}
            <div data-testid="analytics-alerts">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <h3 className="text-sm font-semibold">Alertes</h3>
                {data.alertes_critical > 0 && (
                  <Badge variant="destructive" className="text-[10px]" data-testid="alert-critical-badge">{data.alertes_critical} critique{data.alertes_critical > 1 ? 's' : ''}</Badge>
                )}
                {data.alertes_warning > 0 && (
                  <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-700" data-testid="alert-warning-badge">{data.alertes_warning} warning</Badge>
                )}
              </div>

              {data.alertes.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4" data-testid="no-alerts">Aucune alerte active</p>
              ) : (
                <div className="space-y-2">
                  {data.alertes.map((alert, i) => {
                    const cfg = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
                    return (
                      <div key={alert.item_id || i} className={`border rounded-lg p-2.5 ${cfg.color}`} data-testid={`alert-${alert.item_id || i}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                              <span className="text-xs font-medium">
                                {alert.type === 'promesse_retard' ? 'Promesse en retard' :
                                 alert.type === 'blocage_long' ? 'Blocage long' : 'Inactivite'}
                              </span>
                            </div>
                            <p className="text-sm">{alert.content}</p>
                            {alert.contact_name && <p className="text-[11px] opacity-70">Contact: {alert.contact_name}</p>}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Clock className="w-3 h-3" />
                            <span className="text-xs font-mono">{alert.days_active}j</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function MetricCard({ title, icon: Icon, iconColor, items, testId }) {
  return (
    <div className="border rounded-lg p-3" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <span className="text-sm font-semibold">{title}</span>
      </div>
      <div className="space-y-1">
        {items.map(item => (
          <div key={item.label} className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{item.label}</span>
            <span className={`text-sm ${item.bold ? 'font-bold' : 'font-medium'} ${item.color}`}>{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
